import Head from 'next/head';
import '@packages/shared/core/common/assets/styles/index.scss';
import 'slick-carousel/slick/slick.scss';
import '@packages/shared/core/common/components/common/FavoriteControl/FavoriteControl.scss';
import type { AppProps as AppPropsEntity } from 'next/app';
import { CacheProvider } from '@emotion/react';
import type { EmotionCache } from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme, getDefaultFonts } from '@packages/shared/app/utils';
import MetaDataDefault from '@packages/shared/core/common/components/common/MetaDataDefault';
import PageProvider from '@packages/shared/app/ui/templates/PageProvider';
import { QueryCacheOptionalFallback } from '@packages/shared/core/queryCache/entities';
import createStylesCache from '@packages/shared/core/common/utils/createStylesCache';
import { AutofillScript, OneTrustScripts, GTMScripts, ClarityScript } from '@packages/shared/app/utils/scripts';
import {
  SpritePreloadLink,
  TrustpilotVerificationMeta,
  FacebookVerificationMeta,
} from '@packages/shared/app/utils/head';
import { ROBOTS_META_TAG_KEY, ROBOTS_CONTENT } from '@packages/shared/core/common/constants/robots';
import sprite from '@packages/shared/assets/sprites/sprite.svg';
import { guidanceConfig as config } from 'src/config';

export type AppProps = {
  emotionCache?: EmotionCache;
} & AppPropsEntity<QueryCacheOptionalFallback>;

const clientSideStylesCache = createStylesCache();
const fonts = getDefaultFonts();
const theme = createAppTheme(fonts);

const App = ({ Component, pageProps, emotionCache = clientSideStylesCache }: AppProps) => {
  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={theme}>
        <Head>
          <SpritePreloadLink sprite={sprite} />

          <TrustpilotVerificationMeta trustpilotVerificationId={config.verification.trustpilotVerificationId} />

          <FacebookVerificationMeta facebookVerificationId={config.verification.facebookVerificationId} />

          <meta name="robots" content={ROBOTS_CONTENT.NO_INDEX_NO_FOLLOW} key={ROBOTS_META_TAG_KEY} />
        </Head>

        <MetaDataDefault />

        <PageProvider className={fonts.className} config={config} fallback={pageProps.fallback}>
          <Component {...pageProps} />
        </PageProvider>

        <AutofillScript />

        <OneTrustScripts oneTrustKey={config.oneTrust.key} />

        <GTMScripts gtmId={config.gtmId} />

        <ClarityScript clarityId={config.clarityId} />

        <noscript>You need to enable JavaScript to run this app.</noscript>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default App;
